import React, { useEffect, useState } from "react";
import { Card, Grid, Button, Tab, Tabs } from "@mui/material";
import { Add as AddIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { skillActions } from "store/skill-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchMemberSkills,
  fetchActiveSkills,
  deleteMemberSkill,
  fetchMemberCertifications,
} from "app/services/skill.service";
import { MemberSkillsDataGrid } from "./MemberSkillsDataGrid";
import { CertificationsDataGrid } from "./CertificationsDataGrid";
import AddMemberSkillDialog from "./AddMemberSkillDialog";
import AddCertificationDialog from "./AddCertificationDialog";
import { ConfirmDialog } from "../ConfirmDialog";

export const SkillMatrixMemberView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [editMemberSkill, setEditMemberSkill] = useState(null);
  const [editCert, setEditCert] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [memberCerts, setMemberCerts] = useState([]);

  const memberSkills = useSelector((state) => state.skill.memberSkills);
  const user = useSelector((state) => state.user);
  const memberId = user?.memberId;

  useEffect(() => {
    if (memberId) {
      loadMySkills();
      loadMyCertifications();
    }
    loadAvailableSkills();
  }, [memberId]);

  const loadMySkills = () => {
    if (!memberId) return;
    setIsLoading(true);
    fetchMemberSkills(memberId)
      .then((data) => dispatch(skillActions.setMemberSkills(data)))
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  const loadMyCertifications = () => {
    if (!memberId) return;
    fetchMemberCertifications(memberId)
      .then((data) => setMemberCerts(data))
      .catch((error) => handleAxiosError(error));
  };

  const loadAvailableSkills = () => {
    fetchActiveSkills()
      .then((data) => setAvailableSkills(data))
      .catch((error) => handleAxiosError(error));
  };

  const handleEditSkill = (skill) => {
    setEditMemberSkill(skill);
    setSkillDialogOpen(true);
  };

  const handleDeleteSkill = (skill) => {
    setDeleteItem(skill);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteItem) {
      await deleteMemberSkill(deleteItem.id);
      loadMySkills();
    }
    setDeleteItem(null);
  };

  const handleEditCert = (cert) => {
    setEditCert(cert);
    setCertDialogOpen(true);
  };

  const verifiedCount = memberSkills.filter((ms) => ms.isVerified).length;
  const primaryCount = memberSkills.filter((ms) => ms.isPrimary).length;

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="info">My Skills</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{memberSkills.length}</MDTypography>
              <MDTypography variant="caption">Total Skills</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="success">Verified</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{verifiedCount}</MDTypography>
              <MDTypography variant="caption">Verified Skills</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="warning">Certifications</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{memberCerts.length}</MDTypography>
              <MDTypography variant="caption">My Certifications</MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Main Content with Tabs */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="My Skills" />
                <Tab label="My Certifications" />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => { setEditMemberSkill(null); setSkillDialogOpen(true); }}
                    variant="contained"
                    color="success"
                  >
                    Add Skill
                  </Button>
                </MDBox>
                <MemberSkillsDataGrid
                  memberSkills={memberSkills}
                  isLoading={isLoading}
                  onEdit={handleEditSkill}
                  onDelete={handleDeleteSkill}
                  showActions={true}
                  showVerifyAction={false}
                />
              </MDBox>
            )}
            {tabValue === 1 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => { setEditCert(null); setCertDialogOpen(true); }}
                    variant="contained"
                    color="success"
                  >
                    Add Certification
                  </Button>
                </MDBox>
                <CertificationsDataGrid
                  certifications={memberCerts}
                  isLoading={isLoading}
                  onEdit={handleEditCert}
                  onDelete={() => {}}
                  showActions={true}
                />
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>

      <AddMemberSkillDialog
        open={skillDialogOpen}
        setOpen={setSkillDialogOpen}
        onSuccess={loadMySkills}
        editObject={editMemberSkill}
        skills={availableSkills}
        memberId={memberId}
      />
      <AddCertificationDialog
        open={certDialogOpen}
        setOpen={setCertDialogOpen}
        onSuccess={loadMyCertifications}
        editObject={editCert}
        memberId={memberId}
      />
      <ConfirmDialog
        title="Delete Skill?"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={confirmDelete}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        Are you sure you want to remove this skill from your profile?
      </ConfirmDialog>
    </MDBox>
  );
};

